import Axios from 'axios';
import {
  WEB_API_GET_DOCUMENTS,
  WEB_API_GET_DOCUMENT_BY_ID,
  WEB_API_GET_DOCUMENTS_BY_TITLE,
  WEB_API_GET_SECTIONS_BY_TITLE,
  WEB_API_GET_GLOSSARY_BY_TITLE,
  WEB_API_SEARCH,
  WEB_API_GET_DOC_GLOSSARY_BY_ID,
  WEB_API_GET_RELATED_DOCS_BY_ID,
  WEB_API_GET_RELATED_SECTION_BY_ID,
  WEB_API_GET_SECTIONS_BY_ID,
  WEB_API_GET_SECTION_BY_PAGE_ID,
  WEB_API_GET_SUMMARY_BY_DOC_ID,
  WEB_API_QUERY_DOCUMENTS,
  WEB_API_GET_MY_QUERIES,
  WEB_API_COMPARE,
  WEB_API_GET_RELATED_CONTENT_BY_DOC,
  WEB_API_GET_RELATED_CONTENT_BY_TAXONOMY_ITEM,
  WEB_API_GET_TAXONOMIES,
  WEB_API_GET_TAXONOMY_ITEMS,
  WEB_API_GET_TAXONOMY_ITEM,
  WEB_API_GET_TAXONOMY_ITEM_DOCUMENTS,
} from './webApiConsts';

// Developer-portal (/api) calls. Environment-driven via REACT_APP_WEB_API_BASE_URL — no more
// manual local/deployed toggle. Defaults to the existing deployed developer-API backend
// (staging) to preserve current behavior; converging this onto the main API base is a later step.
const BASE_URL =
  process.env.REACT_APP_WEB_API_BASE_URL ||
  'https://stagingregcityapi.azurewebsites.net/api';

// Exposed for the playground's "copy request URL" share action.
export const WEB_API_BASE_URL = BASE_URL;

/**
 * Base json type header for all Axios calls.
 */
const HEADER = {
  'Content-Type': 'application/json',
};

const axios = Axios.create({
  BASE_URL,
  withCredentials: true,
});

const AUTH_HEADER = (token) => ({
  ...HEADER,
  Authorization: `Bearer ${token}`,
});

/**
 * Fetch documents from WorkingDocuments list
 * @param {string} key SharePoint Access key.
 * @returns The array of published documents
 */
export const GetWebApiDocuments = async (token, version) => {
  try {
    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: WEB_API_GET_DOCUMENTS(version),
      headers: AUTH_HEADER(token),
    });

    return apiResponse.data;
  } catch (error) {
    console.log('ERROR in WEB_API_GET_DOCUMENTS', error);
    return [];
  }
};

export const GetWebApiDocumentsByTitle = async (token, version, title) => {
  try {
    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: WEB_API_GET_DOCUMENTS_BY_TITLE(version, title),
      headers: AUTH_HEADER(token),
    });

    return apiResponse.data;
  } catch (error) {
    console.log('ERROR in WEB_API_GET_DOCUMENTS_BY_TITLE', error.message);
    return [];
  }
};

export const GetWebApiSectionsByTitle = async (token, version, documentId, title) => {
  try {
    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: WEB_API_GET_SECTIONS_BY_TITLE(version, documentId, title),
      headers: AUTH_HEADER(token),
    });

    return apiResponse.data;
  } catch (error) {
    console.log('ERROR in WEB_API_GET_SECTIONS_BY_TITLE', error.message);
    return (error.response && error.response.data) || [];
  }
};

export const GetWebApiGlossaryByTitle = async (token, version, title) => {
  try {
    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: WEB_API_GET_GLOSSARY_BY_TITLE(version, title),
      headers: AUTH_HEADER(token),
    });

    return apiResponse.data;
  } catch (error) {
    console.log('ERROR in WEB_API_GET_GLOSSARY_BY_TITLE', error.message);
    return [];
  }
};

export const GetWebApiSearch = async (token, version, q) => {
  try {
    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: WEB_API_SEARCH(version, q),
      headers: AUTH_HEADER(token),
    });

    return apiResponse.data;
  } catch (error) {
    console.log('ERROR in WEB_API_SEARCH', error.message);
    return [];
  }
};

export const GetWebApiRelatedContentByDoc = async (token, version, documentId) => {
  try {
    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: WEB_API_GET_RELATED_CONTENT_BY_DOC(version, documentId),
      headers: AUTH_HEADER(token),
    });

    return apiResponse.data;
  } catch (error) {
    console.log('ERROR in WEB_API_GET_RELATED_CONTENT_BY_DOC', error.message);
    // Surface the API error envelope (e.g. 404 for an unknown id) rather than a
    // bare [] that the ok-check would read as an empty success.
    return (error.response && error.response.data) || [];
  }
};

export const GetWebApiRelatedContentByTaxonomyItem = async (token, version, taxonomy, itemId) => {
  try {
    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: WEB_API_GET_RELATED_CONTENT_BY_TAXONOMY_ITEM(version, taxonomy, itemId),
      headers: AUTH_HEADER(token),
    });

    return apiResponse.data;
  } catch (error) {
    console.log('ERROR in WEB_API_GET_RELATED_CONTENT_BY_TAXONOMY_ITEM', error.message);
    return (error.response && error.response.data) || [];
  }
};

export const GetWebApiTaxonomies = async (token, version) => {
  try {
    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: WEB_API_GET_TAXONOMIES(version),
      headers: AUTH_HEADER(token),
    });

    return apiResponse.data;
  } catch (error) {
    console.log('ERROR in WEB_API_GET_TAXONOMIES', error.message);
    return (error.response && error.response.data) || [];
  }
};

export const GetWebApiTaxonomyItems = async (token, version, taxonomy) => {
  try {
    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: WEB_API_GET_TAXONOMY_ITEMS(version, taxonomy),
      headers: AUTH_HEADER(token),
    });

    return apiResponse.data;
  } catch (error) {
    console.log('ERROR in WEB_API_GET_TAXONOMY_ITEMS', error.message);
    return (error.response && error.response.data) || [];
  }
};

export const GetWebApiTaxonomyItem = async (token, version, taxonomy, id) => {
  try {
    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: WEB_API_GET_TAXONOMY_ITEM(version, taxonomy, id),
      headers: AUTH_HEADER(token),
    });

    return apiResponse.data;
  } catch (error) {
    console.log('ERROR in WEB_API_GET_TAXONOMY_ITEM', error.message);
    return (error.response && error.response.data) || [];
  }
};

export const GetWebApiTaxonomyItemDocuments = async (token, version, taxonomy, id) => {
  try {
    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: WEB_API_GET_TAXONOMY_ITEM_DOCUMENTS(version, taxonomy, id),
      headers: AUTH_HEADER(token),
    });

    return apiResponse.data;
  } catch (error) {
    console.log('ERROR in WEB_API_GET_TAXONOMY_ITEM_DOCUMENTS', error.message);
    return (error.response && error.response.data) || [];
  }
};

export const GetWebApiDocumentById = async (token, version, id) => {
  try {
    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: WEB_API_GET_DOCUMENT_BY_ID(version, id),
      headers: AUTH_HEADER(token),
    });

    return apiResponse.data;
  } catch (error) {
    console.log('ERROR in WEB_API_GET_DOCUMENT_BY_ID', error.message);
    return [];
  }
};

export const GetWebApiSectionsById = async (token, version, id) => {
  try {
    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: WEB_API_GET_SECTIONS_BY_ID(version, id),
      headers: AUTH_HEADER(token),
    });

    return apiResponse.data;
  } catch (error) {
    console.log('ERROR in WEB_API_GET_SECTIONS_BY_ID', error.message);
    return [];
  }
};

export const GetWebApiRelateDocsById = async (token, version, id) => {
  try {
    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: WEB_API_GET_RELATED_DOCS_BY_ID(version, id),
      headers: AUTH_HEADER(token),
    });

    return apiResponse.data;
  } catch (error) {
    console.log('ERROR in WEB_API_GET_RELATED_DOCS_BY_ID', error.message);
    // Return the server's error body (or empty) — never the raw axios Error object,
    // which the Explorer would otherwise mis-read as a successful response.
    return (error.response && error.response.data) || [];
  }
};

export const GetWebApiDocGlossaryById = async (token, version, id) => {
  try {
    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: WEB_API_GET_DOC_GLOSSARY_BY_ID(version, id),
      headers: AUTH_HEADER(token),
    });

    return apiResponse.data;
  } catch (error) {
    console.log('ERROR in WEB_API_GET_DOC_GLOSSARY_BY_ID', error.message);
    return [];
  }
};

export const GetWebApiSectionByDocId = async (token, version, id) => {
  try {
    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: WEB_API_GET_SECTION_BY_PAGE_ID(version, id),
      headers: AUTH_HEADER(token),
    });

    return apiResponse.data;
  } catch (error) {
    console.log('ERROR in WEB_API_GET_SECTION_BY_PAGE_ID', error.message);
    return [];
  }
};

export const GetWebApiGetRelatedSectionsId = async (token, version, id) => {
  try {
    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: WEB_API_GET_RELATED_SECTION_BY_ID(version, id),
      headers: AUTH_HEADER(token),
    });

    return apiResponse.data;
  } catch (error) {
    console.log('ERROR in WEB_API_GET_RELATED_SECTION_BY_ID', error.message);
    return [];
  }
};

export const GetWebApiSummaryByDocId = async (token, version, id) => {
  try {
    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: WEB_API_GET_SUMMARY_BY_DOC_ID(version, id),
      headers: AUTH_HEADER(token),
    });

    return apiResponse.data;
  } catch (error) {
    console.log('ERROR in WEB_API_GET_SUMMARY_BY_DOC_ID', error.message);
    return [];
  }
};

// POST the AI query. Returns the raw answer body on success, or the server's error
// envelope (validation / quota / unavailable) so the response panel can show it.
export const QueryWebApiDocuments = async (token, version, body) => {
  try {
    const apiResponse = await axios({
      method: 'POST',
      baseURL: BASE_URL,
      url: WEB_API_QUERY_DOCUMENTS(version),
      headers: AUTH_HEADER(token),
      data: body,
    });

    return apiResponse.data;
  } catch (error) {
    console.log('ERROR in WEB_API_QUERY_DOCUMENTS', error.message);
    return (
      (error.response && error.response.data) || {
        Succeeded: false,
        Message: error.message,
      }
    );
  }
};

export const GetWebApiMyQueries = async (token, version, take) => {
  try {
    const apiResponse = await axios({
      method: 'GET',
      baseURL: BASE_URL,
      url: WEB_API_GET_MY_QUERIES(version, take),
      headers: AUTH_HEADER(token),
    });

    return apiResponse.data;
  } catch (error) {
    console.log('ERROR in WEB_API_GET_MY_QUERIES', error.message);
    return (error.response && error.response.data) || [];
  }
};

// POST an AI document comparison. This can take a minute or two, so allow a long timeout.
export const CompareWebApiDocuments = async (token, version, body) => {
  try {
    const apiResponse = await axios({
      method: 'POST',
      baseURL: BASE_URL,
      url: WEB_API_COMPARE(version),
      headers: AUTH_HEADER(token),
      data: body,
      timeout: 240000,
    });

    return apiResponse.data;
  } catch (error) {
    console.log('ERROR in WEB_API_COMPARE', error.message);
    return (
      (error.response && error.response.data) || {
        Succeeded: false,
        Message: error.message,
      }
    );
  }
};
